import React from "react";
import Link from "next/link";
import { useSelector } from "react-redux";

import { Title, Section, Steps, LinkDecor } from "@vshp/ui-components";
import { fetchOrgSettings } from "../actions/settings";
import { fetchPrograms } from "../actions/programs";
import { fetchOrgContacts } from "../actions/contacts";
import { fetchPostsRecent } from "../actions/posts";
import { fetchVideos } from "../actions/videos";
import { fetchReviews, fetchReviewsSuccess } from "../actions/reviews";

import Applicants from "../components/shared/Applicants";
import Contacts from "../components/shared/Contacts";
import Accreditation from "../components/shared/Accreditation";
import Consultation from "../components/shared/Consultation";
import Videos from "../components/shared/Videos";
import Programs from "../components/shared/Programs";
// import Greeting from "../components/shared/Greeting";
import Teachers from "../components/shared/Teachers";
import Certificates from "../components/shared/Certificates";
import Reviews from "../components/shared/Reviews";
import Partners from "../components/shared/Partners";
import Faq from "../components/shared/Faq";
import Offer from "../components/shared/Offer";
import Life from "../components/shared/Life";
import VideosInfo from "../components/shared/VideosInfo";
import PostsRecent from "../components/shared/PostsRecent";
import Successes from "../components/shared/Successes";
import Education from "../components/shared/Education";
import Events from "../components/shared/Events";

import { wrapper } from "../store";

export const getServerSideProps = wrapper.getServerSideProps((store) => async () => {
  await store.dispatch(fetchOrgSettings());
  await store.dispatch(fetchPrograms());
  await store.dispatch(fetchOrgContacts());
  await store.dispatch(fetchPostsRecent());
  await store.dispatch(fetchVideos());
  await store.dispatch(fetchReviews());
  await store.dispatch(fetchReviewsSuccess());
});

function Home() {
  const { settingsOrg } = useSelector((state) => state.settingsOrg);
  const { settings } = useSelector((state) => state.settings);

  const pagesChildren = settingsOrg?.pages?.[0]?.children || [];
  const educationChild = pagesChildren.find((child) => child.slug === "education");

  return (
    <div className="main">
      <div className="page__header main__intro intro">
        <div className="intro__video">
          <video loop autoPlay muted playsInline preload="auto">
            <source src="/video/vshp.ogv" type='video/ogg; codecs="theora, vorbis"' />
            <source src="/video/vshp.webm" type='video/webm; codecs="vp8, vorbis"' />
            <source src="/video/vshp.mp4" type='video/mp4; codecs="avc1.42E01E, mp4a.40.2"' />
          </video>
        </div>
        <div className="page__header-body page__header-body_center">
          <Title decor={false} className="page__header-title main__intro-title">
            <span dangerouslySetInnerHTML={{ __html: settingsOrg.title }}></span>
          </Title>
          <div className="page__header-subtitle" dangerouslySetInnerHTML={{ __html: settingsOrg.description }}></div>
          <Link className="btn btn_primary page__header-btn" href={settingsOrg.pages[0].children[0].slug}>
            {settingsOrg.pages[0].children[0].title}
          </Link>
        </div>
      </div>

      <div className="page__blocks">
        {settings.events !== null && settings?.events?.length > 0 && <Events events={settings.events} />}

        <Section className="wysiwyg" border={true}>
          <Title type="h2">О лицее</Title>
          <Section.BodySmall>
            <p>
              Лицей &laquo;Высшая школа предпринимательства&raquo;&nbsp;&mdash; одно из&nbsp;старейших частных учебных заведений Российской Федерации, имеющее уже 30-ти летнюю историю. Это место
              сформированных и&nbsp;глубоко укоренившихся традиций качества образовательного и&nbsp;воспитательного процессов. Талантливые педагоги, малокомплектные классы с&nbsp;индивидуальным
              подходом к&nbsp;учащимся, усиленное внимание к&nbsp;творческим и&nbsp;деловым способностям учеников, углубленное изучение иностранных языков и&nbsp;бизнес-дисциплин, активные занятия
              спортом, воспитательный процесс с&nbsp;патриотическим уклоном, большое внимание к&nbsp;безопасности учащихся,&nbsp;&mdash; все это обязательные составляющие учебного процесса
              в&nbsp;лицее &laquo;Высшая школа предпринимательства&raquo;. Начиная с&nbsp;9&nbsp;класса, ученики лицея проходят усиленную подготовку к&nbsp;сдаче государственных аттестационных
              мероприятий (ОГЭ и&nbsp;ЕГЭ). Такой подход обеспечивает возможность выпускать из&nbsp;стен лицея будущих лидеров общественной и&nbsp;деловой жизни нашей страны, обладающих не&nbsp;только
              первоклассной общеобразовательной подготовкой, но&nbsp;и&nbsp;образцовыми нравственными качествами.
            </p>
          </Section.BodySmall>
        </Section>
        <Section className="note wysiwyg">
          <Title type="h2">Преимущества лицея</Title>

          <div className="list-cards">
            <div className="list-cards__item">Классы до&nbsp;16&nbsp;человек</div>
            <div className="list-cards__item">Углубленное изучение иностранных языков</div>
            <div className="list-cards__item">Усиленная физическая подготовка</div>
            <div className="list-cards__item">Индивидуальный подход к&nbsp;каждому ребенку</div>
            <div className="list-cards__item">Квалифицированный педагогический состав</div>
            <div className="list-cards__item">Благоприятный психологический климат</div>
            <div className="list-cards__item">Разностороннее развитие детей</div>
            <div className="list-cards__item">Тесный контакт классного руководителя с&nbsp;родителями</div>
            <div className="list-cards__item">Высокий уровень подготовки к&nbsp;ЕГЭ и&nbsp;ОГЭ</div>
            <div className="list-cards__item">Возможность параллельного обучения в&nbsp;школе и&nbsp;колледже</div>
            <div className="list-cards__item">Программа дистанционного обучения</div>
            <div className="list-cards__item">Патриотическое воспитание лицеистов</div>
            <div className="list-cards__item">Обеспечение необходимыми учебными материалами</div>
            <div className="list-cards__item">Современная и&nbsp;развитая материально-техническая база</div>
            <div className="list-cards__item">Полноценное питание</div>
            <div className="list-cards__item">Безопасное пребывание на&nbsp;территории</div>
            <div className="list-cards__item">Группы продленного дня</div>
            <div className="list-cards__item">Дополнительные занятия по&nbsp;подготовке к&nbsp;ОГЭ и&nbsp;ЕГЭ</div>
          </div>
        </Section>
        <Successes />
        <Consultation />
        <Videos />
        {/*<Greeting />*/}
        <Programs />
        <VideosInfo border={!educationChild} />

        {educationChild && <Education slug={educationChild.slug} />}

        <Section border={true}>
          <Title type="h2">Как проходит обучение</Title>
          <Steps>
            <Steps.Item title="Знакомство" description="Сотрудник приемной комиссии проведет для вас экскурсию по&nbsp;учебному заведению и&nbsp;расскажет историю лицея."></Steps.Item>
            <Steps.Item title="Поступление" description="Для поступления достаточно предоставить только пакет необходимых документов." />
            <Steps.Item title="Обучение" description="Занятия проходят 5&nbsp;дней в&nbsp;неделю. По&nbsp;субботам дети отдыхают.">
              <br />
              При обучении по&nbsp;программе полупансиона ребёнок находится в&nbsp;здании лицея с&nbsp;8:00 или 8:15 до&nbsp;18:00.
            </Steps.Item>
            <Steps.Item
              title="Дополнительные занятия"
              description="Лицеисты занимаются в&nbsp;Центре по&nbsp;подготовке к&nbsp;ОГЭ и&nbsp;ЕГЭ &laquo;Высшей школы предпринимательства&raquo;. Методику подготовки по&nbsp;каждому предмету разрабатывают действующие репетиторы&nbsp;&mdash; эксперты ЕГЭ и&nbsp;ОГЭ,&nbsp;&mdash; которые подготавливают учащихся к&nbsp;экзаменам по&nbsp;русскому языку, математике, обществознанию, географии, физике, истории, информатике, химии, биологии, английскому и&nbsp;немецкому языкам, литературе, музыкальному и&nbsp;художественному искусству, физическому развитию."
            />
            <Steps.Item
              title="Внеклассная жизнь"
              description="Лицей ориентирован на&nbsp;патриотическое воспитание, спортивное и&nbsp;творческое развитие, поэтому каждый месяц учителя организуют для детей культурные мероприятия или соревнования."
            />
            <Steps.Item
              title="Аттестация"
              description="В&nbsp;лицее организованы дополнительные курсы по&nbsp;подготовке к&nbsp;ОГЭ и&nbsp;ЕГЭ. Ваш ребёнок может записаться сразу на&nbsp;несколько курсов."
            />
            <Steps.Item
              title="Дальнейшее образование"
              description="Комплекс &laquo;Высшая школа предпринимательства&raquo; предоставляет выпускникам своего лицея возможность поступить в&nbsp;колледж или институт без экзаменов."
            />
          </Steps>
        </Section>
        <Life />
        <Teachers />
        <Certificates />
        <Section border={true}>
          <Title type="h2">ОФИЦИАЛЬНЫЕ ДОКУМЕНТЫ</Title>
          <LinkDecor link="https://islod.obrnadzor.gov.ru/rlic/details/7d929074-d0c1-e3e7-cab5-15017fe3cf60/">Лицензия на осуществление образовательной деятельности</LinkDecor>
          <LinkDecor link="https://islod.obrnadzor.gov.ru/accredreestr/details/727c384c-3cf4-bede-c400-532ee94a084c/1/">Свидетельство о государственной аккредитации</LinkDecor>
          <LinkDecor link="https://cdn.vshp.online/docs/845c/Ofitsialnaya_prezentatsiya_Chastnogo_obscheobrazovatelnogo_uchrezhdeniya__Vysshaya_shkola_predprinimatelstva_.pdf">
            Официальная презентация Частного общеобразовательного учреждения &laquo;Высшая школа предпринимательства&raquo;
          </LinkDecor>
        </Section>
        <Reviews />
        <Partners />
        <Applicants slug={settingsOrg.pages[0].children[1].slug} />
        <Accreditation />
        <Faq />
        <Offer />
        <PostsRecent />
        <Contacts />
      </div>
    </div>
  );
}

export default Home;
